import { ref } from 'vue'
import axios from '@/utils/apiClient'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ElLoading, ElMessageBox, ElNotification } from 'element-plus'
import { AxiosError } from 'axios'
import { ApiResponse, ValidationErrorResponse } from '@/types/apiResponse'
import { PaginationResponse } from '@/types/PaginationResponse'
import { usePagination } from '@/composables/usePagination'
import { useI18n } from 'vue-i18n'
import swal from 'sweetalert2'
import type { ApiOptions } from '@/types/ApiOptions'

// Helper function to safely extract error message string from any response shape
function extractErrorMessage(data: any): string {
    if (!data) return 'Unknown server error'

    if (typeof data === 'string') return data

    if ('message' in data && typeof data.message === 'string') return data.message

    if ('error' in data && typeof data.error === 'string') return data.error

    if ('title' in data && typeof data.title === 'string') return data.title

    if ('errors' in data && typeof data.errors === 'object') {
        const messages = Object.values(data.errors)
            .flat()
            .filter((m): m is string => typeof m === 'string')
        if (messages.length > 0) return messages[0]
    }

    return 'Unknown server error'
}

export const useApi = <T, TResponse>(apiOptions: ApiOptions) => {
    const { t } = useI18n()
    const dialog = ref(apiOptions.dialog || false)

    const fetchData = async (): Promise<ApiResponse<TResponse>> => {
        const res = await axios.get(apiOptions.endpoint, { params: apiOptions.queryParams })
        return res.data
    }
    const getItemById = async (id: number | string): Promise<TResponse> => {
        const { data } = await axios.get(`${apiOptions.endpoint}/${id}`)
        return data
    }
    const postData = async (data: T) => {
        const res = await axios.post<ApiResponse<TResponse>>(apiOptions.endpoint, data)
        return res.data
    }

    const putData = async (id: number, data: T) => {
        const res = await axios.put<ApiResponse<T>>(`${apiOptions.endpoint}/${id}`, data)
        return res.data
    }

    const removeData = async (id: number) => {
        const res = await axios.delete<ApiResponse<T>>(`${apiOptions.endpoint}/${id}`)
        return res.data
    }

    // Query for fetching data
    const { data, isFetching, refetch } = useQuery<ApiResponse<TResponse>, Error>({
        queryKey: [apiOptions.endpoint, apiOptions.queryParams],
        queryFn: fetchData,
    })

    const { mutateAsync: postMutation, isPending: isPosting } = useMutation({
        mutationFn: postData,
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<any>

            if (axiosError.response?.data) {
                const data = axiosError.response.data

                // Type guard for ValidationErrorResponse
                const isValidationError = (data: any): data is ValidationErrorResponse => {
                    return (
                        typeof data === 'object' &&
                        data !== null &&
                        'errors' in data &&
                        'title' in data &&
                        typeof data.errors === 'object'
                    )
                }

                if (isValidationError(data)) {
                    for (const field in data.errors) {
                        let html = '<div class="bg-yellow-100 text-red rounded-sm text-sm">'
                        data.errors[field].forEach(message => {
                            html += `${field}: ${message}<br/>`
                        })
                        html += '</div>'
                        swal.fire({
                            icon: 'error',
                            confirmButtonColor: '#e4736e',
                            html: html,
                        })
                    }
                } else {
                    const message = extractErrorMessage(data)
                    swal.fire({
                        icon: 'error',
                        confirmButtonColor: '#e4736e',
                        html: `<div class="bg-yellow-100 text-red rounded-sm text-sm">${message}</div>`,
                    })
                }
            } else {
                const message = axiosError?.data?.message || 'Request failed'
                swal.fire({
                    icon: 'error',
                    confirmButtonColor: '#e4736e',
                    html: `<div class="bg-yellow-100 text-red rounded-sm text-sm">${message}</div>`,
                })
            }
        },
        onSuccess: () => {
            refetch()
            dialog.value = false
            ElNotification.success('Success')
        },
    })

    const { mutateAsync: putMutation, isPending: isPutting } = useMutation({
        mutationFn: ({ id, data }: { id: number; data: T }) => putData(id, data),
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<any>
            if (axiosError.response?.data) {
                const data = axiosError.response.data

                const isValidationError = (data: any): data is ValidationErrorResponse => {
                    return (
                        typeof data === 'object' &&
                        data !== null &&
                        'errors' in data &&
                        'title' in data &&
                        Array.isArray(data.errors[Object.keys(data.errors)[0]])
                    )
                }

                if (isValidationError(data)) {
                    const messages = Object.values(data.errors).flat()
                    ElNotification.error(messages[0] || data.title)
                } else {
                    const message = extractErrorMessage(data)
                    ElNotification.error(message)
                }
            } else {
                ElNotification.error(axiosError.message || 'Request failed')
            }
        },
        onSuccess: response => {
            refetch()
            dialog.value = false
            const message = response.message || 'Successful'
            ElNotification.success(message)
        },
    })

    const { mutate: deleteMutation, isPending: isDeleting } = useMutation({
        mutationFn: removeData,
        onMutate: () => {
            const loading = ElLoading.service({
                lock: true,
                text: 'Deleting...',
                background: 'rgba(0, 0, 0, 0.5)',
            })
            return { loading }
        },
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<ApiResponse<any>>
            if (axiosError.response?.data) {
                const apiResponse = axiosError.response.data
                // ElMessageBox({
                //   type: 'error',
                //   confirmButtonText: t('OK'),
                //   message: extractErrorMessage(apiResponse),
                // })
            } else {
                ElNotification.error(axiosError.message || 'Request failed')
            }
        },
        onSuccess: response => {
            refetch()
            const message = response.message ?? 'Successful'
            ElNotification.success(message)
        },
        onSettled: (_, __, ___, context) => {
            context?.loading?.close()
        },
    })

    return {
        data,
        isFetching,
        refetch,
        getItemById,
        postMutation,
        putMutation,
        deleteMutation,
        isPosting,
        isPutting,
        isDeleting,
        dialog,
    }
}

export const useApiWithPagination = <T, TResponse>({ endpoint, queryParams = {}, pagination = true }: ApiOptions) => {
    const { t } = useI18n()
    const dialog = ref(false)
    const { page, pageSize, searchInput, search, handlePageChange } = usePagination()

    const fetchData = async (): Promise<PaginationResponse<TResponse>> => {
        const params = pagination
            ? {
                  ...queryParams,
                  page: page.value,
                  pageSize: pageSize.value,
                  search: search.value,
              }
            : queryParams
        const res = await axios.get(endpoint, { params })
        return res.data
    }

    const getItemById = async (id: number | string): Promise<T> => {
        const { data } = await axios.get(`${endpoint}/${id}`)
        return data
    }

    const postData = (data: T) => axios.post<ApiResponse<T>>(endpoint, data).then(res => res.data)

    const putData = (data: T & { id: number }) =>
        axios.put<ApiResponse<T>>(`${endpoint}/${data.id}`, data).then(res => res.data)

    const removeData = (id: number) => axios.delete<ApiResponse<T>>(`${endpoint}/${id}`).then(res => res.data)

    const { data, isFetching, refetch } = useQuery<PaginationResponse<TResponse>, Error>({
        queryKey: [endpoint, page, pageSize, search, queryParams],
        queryFn: fetchData,
    })

    const { mutate: postMutation, isPending: isPosting } = useMutation({
        mutationFn: postData,
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<ApiResponse<any>>
            if (axiosError.response?.data) {
                const apiResponse = axiosError.response.data
                ElNotification.error(extractErrorMessage(apiResponse))
            } else {
                ElNotification.error(axiosError.message || 'Request failed')
            }
        },
        onSuccess: () => {
            refetch()
            //dialog.value = false
            ElNotification.success('Success')
        },
    })

    const { mutate: putMutation, isPending: isPutting } = useMutation({
        mutationFn: putData,
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<any>

            if (axiosError.response?.data) {
                const data = axiosError.response.data

                const isValidationError = (data: any): data is ValidationErrorResponse => {
                    return (
                        typeof data === 'object' &&
                        data !== null &&
                        'errors' in data &&
                        'title' in data &&
                        Array.isArray(data.errors[Object.keys(data.errors)[0]])
                    )
                }

                if (isValidationError(data)) {
                    const messages = Object.values(data.errors).flat()
                    ElNotification.error(messages[0] || data.title)
                } else {
                    ElNotification.error(extractErrorMessage(data))
                }
            } else {
                ElNotification.error(axiosError.message || 'Request failed')
            }
        },
        onSuccess: () => {
            refetch()
            dialog.value = false
            ElNotification.success('Success')
        },
    })

    const { mutate: deleteMutation, isPending: isDeleting } = useMutation({
        mutationFn: removeData,
        onError: (error: unknown) => {
            const axiosError = error as AxiosError<ApiResponse<any>>
            if (axiosError.response?.data) {
                const apiResponse = axiosError.response.data
                // ElMessageBox({
                //   type: 'error',
                //   confirmButtonText: t('OK'),
                //   message: extractErrorMessage(apiResponse),
                // })
            } else {
                ElNotification.error(axiosError.message || 'Request failed')
            }
        },
        onSuccess: () => {
            refetch()
            ElNotification.success('Delete Successful')
        },
    })

    return {
        data,
        isFetching,
        refetch,
        getItemById,
        postMutation,
        putMutation,
        deleteMutation,
        isPosting,
        isPutting,
        isDeleting,
        page,
        pageSize,
        searchInput,
        search,
        handlePageChange,
        dialog,
        fetchData,
    }
}
